import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { useSecurityStatus } from "../../hooks/useSecurity";
import { TotpSection } from "./TotpSection";
import { PinSection } from "./PinSection";
import { ContactSection } from "./ContactSection";
import { PasswordSection } from "./PasswordSection";
import { SessionsSection } from "./SessionsSection";
import { ActivitySection } from "./ActivitySection";

const SECTIONS = [
  { id: "totp", title: "Verificación en 2 pasos (Google Authenticator)" },
  { id: "pin", title: "PIN de recuperación" },
  { id: "contact", title: "Avisos y recuperación (WhatsApp y correo)" },
  { id: "password", title: "Contraseña" },
  { id: "sessions", title: "Sesiones y dispositivos de confianza" },
  { id: "activity", title: "Actividad reciente" },
];

// «Seguridad»: todo lo de la cuenta en un solo lugar. Cada sección se abre y se cierra; `focus` abre una de entrada (por ejemplo, tras recuperar el acceso).
export function SecurityDialog({ open, onOpenChange, focus = null }) {
  const { data: status, isLoading, isError } = useSecurityStatus(open);
  const [opened, setOpened] = useState(focus || "totp");
  useEffect(() => { if (open) setOpened(focus || "totp"); }, [open, focus]);
  const badge = (ok) => (ok ? "✅" : "🟡");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="security-dialog" className="max-w-xl">
        <DialogHeader>
          <DialogTitle>🔐 Seguridad</DialogTitle>
          <DialogDescription>Protege el acceso al panel: 2 pasos, PIN, avisos, contraseña y dispositivos.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[65vh] overflow-y-auto pr-1 space-y-2">
          {isLoading && <p className="text-sm text-muted-foreground">Cargando…</p>}
          {isError && <p data-testid="security-load-error" className="text-sm text-amber-500">No se pudo leer el estado de seguridad.</p>}
          {status && (
            <>
              <p data-testid="security-summary" className="text-xs text-muted-foreground">
                {badge(status.totp_enabled)} 2 pasos · {badge(status.pin_set)} PIN · {badge(!!status.whatsapp_to && status.whatsapp_ready)} WhatsApp · {badge(!!status.recovery_email && status.email_ready)} Correo
              </p>
              {SECTIONS.map((section) => (
                <div key={section.id} className="rounded-lg border border-border">
                  <button type="button" data-testid={`section-${section.id}`} aria-expanded={opened === section.id} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium" onClick={() => setOpened(opened === section.id ? "" : section.id)}>
                    {section.title}
                    <span className="text-muted-foreground">{opened === section.id ? "▾" : "▸"}</span>
                  </button>
                  {opened === section.id && (
                    <div className="border-t border-border p-3">
                      {section.id === "totp" && <TotpSection status={status} />}
                      {section.id === "pin" && <PinSection status={status} />}
                      {section.id === "contact" && <ContactSection status={status} />}
                      {section.id === "password" && <PasswordSection status={status} />}
                      {section.id === "sessions" && <SessionsSection status={status} />}
                      {section.id === "activity" && <ActivitySection />}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
